import {
  collection, getDocs, query, limit, orderBy, QuerySnapshot, DocumentData,
  where, deleteDoc, doc, addDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { dataCache, CACHE_TTL } from './cache/dataCache';
import { CACHE_KEYS, buildSearchCacheKey } from './cache/cacheKeys';
import { DashboardService } from './DashboardService';
import { BarcodeLookupService } from './BarcodeLookupService';

export interface Product {
  id: string;
  urun_adi: string;
  expirationDate?: string;
  status?: string;
  barkod?: string;
  categoryId?: string;
  createdAt?: string;
}

export interface PendingProduct extends Product {
  addedBy?: string;
}

export interface ProductFilter {
  searchTerm?: string;
  category?: string;
  status?: string;
}

export class ProductError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProductError';
  }
}

export class ProductService {
  private static ITEMS_PER_PAGE = 10;

  static async getPendingProducts(): Promise<PendingProduct[]> {
    try {
      const querySnapshot = await getDocs(collection(db, 'pending_products'));
      return this.mapQueryToProducts(querySnapshot) as PendingProduct[];
    } catch (error) {
      console.error('Bekleyen ürünler yüklenirken hata:', error);
      return [];
    }
  }

  static invalidatePendingProducts(): void {
    dataCache.invalidate(CACHE_KEYS.pendingProducts);
    DashboardService.invalidateStats();
  }

  static async deletePendingProduct(productId: string): Promise<boolean> {
    try {
      await deleteDoc(doc(db, 'pending_products', productId));
      this.invalidatePendingProducts();
      return true;
    } catch (error) {
      console.error('Ürün silinirken hata:', error);
      return false;
    }
  }

  static async searchProducts(filter: ProductFilter): Promise<{ products: Product[]; hasMore: boolean }> {
    try {
      if (!filter.searchTerm && !filter.category && !filter.status) {
        return { products: [], hasMore: false };
      }

      const cacheKey = buildSearchCacheKey(filter);
      const cached = dataCache.get<{ products: Product[]; hasMore: boolean }>(cacheKey, CACHE_TTL.searchResults);
      if (cached) return cached;

      const baseQuery = collection(db, 'products');
      let result: { products: Product[]; hasMore: boolean };

      if (filter.searchTerm) {
        const searchTerm = filter.searchTerm.toLowerCase();
        let q = query(baseQuery, where('barkod', '==', searchTerm), limit(this.ITEMS_PER_PAGE));
        let querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          q = query(
            baseQuery,
            where('urun_adi', '>=', searchTerm),
            where('urun_adi', '<=', searchTerm + '\uf8ff'),
            limit(this.ITEMS_PER_PAGE)
          );
          querySnapshot = await getDocs(q);
        }

        const products = this.mapQueryToProducts(querySnapshot);
        result = { products, hasMore: querySnapshot.docs.length === this.ITEMS_PER_PAGE };
      } else {
        const constraints = [];
        if (filter.category) constraints.push(where('categoryId', '==', filter.category));
        if (filter.status) constraints.push(where('status', '==', filter.status));

        const q = query(baseQuery, ...constraints, orderBy('createdAt', 'desc'), limit(this.ITEMS_PER_PAGE));
        const querySnapshot = await getDocs(q);
        const products = this.mapQueryToProducts(querySnapshot);
        result = { products, hasMore: querySnapshot.docs.length === this.ITEMS_PER_PAGE };
      }

      dataCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Ürün araması sırasında hata:', error);
      return { products: [], hasMore: false };
    }
  }

  static async getLastProductId(force = false): Promise<number> {
    if (!force) {
      const cached = dataCache.get<number>(CACHE_KEYS.lastProductId, CACHE_TTL.lastProductId);
      if (cached !== null) return cached;
    }

    const productsRef = collection(db, 'products');
    const q = query(productsRef, orderBy('id', 'desc'), limit(1));
    const querySnapshot = await getDocs(q);
    const lastId = querySnapshot.empty ? 0 : (querySnapshot.docs[0].data().id || 0);
    dataCache.set(CACHE_KEYS.lastProductId, lastId);
    return lastId;
  }

  static async addProduct({ urun_adi, barkod }: { urun_adi: string; barkod: string }) {
    const trimmedName = urun_adi.trim();
    const trimmedBarkod = barkod.trim();

    if (!trimmedName) {
      throw new ProductError('Ürün adı gerekli.');
    }

    if (!trimmedBarkod) {
      throw new ProductError('Barkod gerekli.');
    }

    const exists = await BarcodeLookupService.isBarcodeExisting(trimmedBarkod);
    if (exists) {
      throw new ProductError('Bu barkod zaten kayıtlı.');
    }

    const lastId = await this.getLastProductId();
    const newId = lastId + 1;
    const newProduct = { id: newId, urun_adi: trimmedName, barkod: trimmedBarkod };

    await addDoc(collection(db, 'products'), newProduct);

    dataCache.set(CACHE_KEYS.lastProductId, newId);
    BarcodeLookupService.markAsExisting(trimmedBarkod);
    DashboardService.invalidateStats();
    dataCache.invalidatePattern(/^products:search:/);

    return newProduct;
  }

  static async approvePendingProduct(
    pendingProductId: string,
    { urun_adi, barkod }: { urun_adi: string; barkod: string },
  ) {
    const newProduct = await this.addProduct({ urun_adi, barkod });

    try {
      await deleteDoc(doc(db, 'pending_products', pendingProductId));
      this.invalidatePendingProducts();
    } catch (error) {
      console.error('Bekleyen ürün silinirken hata:', error);
      throw new ProductError('Ürün eklendi ancak bekleyen listeden kaldırılamadı.');
    }

    return newProduct;
  }

  /** Toplu eklemede lastId tek sefer çekilir, her ürün için tekrar sorgulanmaz */
  static async addProductsBulk(items: { urun_adi: string; barkod: string }[]): Promise<{
    successCount: number;
    alreadyCount: number;
    errorCount: number;
    addedBarcodes: string[];
  }> {
    let successCount = 0;
    let alreadyCount = 0;
    let errorCount = 0;
    const addedBarcodes: string[] = [];

    const barcodes = items.map((item) => item.barkod);
    const existingSet = await BarcodeLookupService.checkExistingBarcodes(barcodes);

    let lastId = await this.getLastProductId();
    const productsRef = collection(db, 'products');

    for (const item of items) {
      if (existingSet.has(item.barkod)) {
        alreadyCount++;
        continue;
      }

      try {
        lastId += 1;
        await addDoc(productsRef, { id: lastId, barkod: item.barkod, urun_adi: item.urun_adi });
        BarcodeLookupService.markAsExisting(item.barkod);
        existingSet.add(item.barkod);
        addedBarcodes.push(item.barkod);
        successCount++;
      } catch {
        lastId -= 1;
        errorCount++;
      }
    }

    if (successCount > 0) {
      dataCache.set(CACHE_KEYS.lastProductId, lastId);
      DashboardService.invalidateStats();
      dataCache.invalidatePattern(/^products:search:/);
    }

    return { successCount, alreadyCount, errorCount, addedBarcodes };
  }

  private static mapQueryToProducts(querySnapshot: QuerySnapshot<DocumentData>): Product[] {
    return querySnapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    } as Product));
  }
}
